import { useEffect, useState, useCallback } from "react";
import { crmApi } from "@/lib/crm";
import { Card, Btn, Input, SectionTitle, Empty, Pill } from "./kit";

export function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [smsTo, setSmsTo] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(() => {
    crmApi("/messages").then(setMessages).catch(() => {});
    crmApi("/customers").then((c) => { setCustomers(c); if (c.length && !to) setTo(c[0].email); }).catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const sendEmail = async (e) => {
    e.preventDefault();
    setNote("");
    try {
      await crmApi("/messages/send", { method: "POST", body: { customer_email: to, subject, body } });
      setNote("Email sent and logged");
      setSubject(""); setBody("");
      load();
    } catch (err) { setNote(err.response?.data?.detail || "Send failed"); }
  };
  const sendSms = async (e) => {
    e.preventDefault();
    setNote("");
    try {
      await crmApi("/messages/sms", { method: "POST", body: { to: smsTo, body: smsBody } });
      setNote("SMS sent and logged");
      setSmsTo(""); setSmsBody("");
      load();
    } catch (err) { setNote(err.response?.data?.detail || "SMS failed"); }
  };

  return (
    <div className="space-y-6" data-testid="crm-messages">
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <SectionTitle>Email composer</SectionTitle>
          <form onSubmit={sendEmail} className="space-y-3">
            <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="compose-to"
              className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-sm outline-none">
              {customers.length === 0 && <option value="">No customers yet</option>}
              {customers.map((c) => <option key={c.id} value={c.email}>{c.email}</option>)}
            </select>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" required data-testid="compose-subject" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message…" required rows={4}
              data-testid="compose-body"
              className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
            <Btn type="submit" data-testid="compose-send">Send email</Btn>
          </form>
        </Card>
        <Card>
          <SectionTitle>SMS</SectionTitle>
          <form onSubmit={sendSms} className="space-y-3">
            <Input value={smsTo} onChange={(e) => setSmsTo(e.target.value)} placeholder="+1…" required data-testid="sms-to" />
            <Input value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder="Message…" required data-testid="sms-body" />
            <Btn type="submit" data-testid="sms-send">Send SMS</Btn>
          </form>
          <p className="text-xs text-slate-500 mt-4">SMS sends through your provider adapter. Until SMS_PROVIDER_URL is configured, sends return a clear configuration error.</p>
        </Card>
      </div>
      {note && <p className="text-sm text-[#f97316]" data-testid="messages-note">{note}</p>}
      <Card>
        <SectionTitle>Message history</SectionTitle>
        {messages.length === 0 ? <Empty>No messages logged yet</Empty> : (
          <ul className="space-y-2.5">
            {messages.map((m) => (
              <li key={m.id} className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
                <Pill tone={m.direction === "inbound" ? "green" : "orange"}>{m.direction}</Pill>
                <Pill>{m.channel}</Pill>
                <span className="text-sm text-slate-300">{m.to || m.from}</span>
                <span className="text-sm text-slate-500 flex-1 truncate">{m.subject || m.body}</span>
                <span className="text-xs text-slate-600">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

const RULE_LABELS = {
  post_purchase_thankyou: "Post-purchase thank-you email",
  vip_tagging: "Repeat-buyer VIP tagging",
  abandoned_recovery: "Automated abandoned-checkout recovery (consent-gated)",
};

export function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(() => {
    crmApi("/reviews").then(setReviews).catch(() => {});
    crmApi("/orders").then((o) => {
      const paid = o.filter((t) => t.payment_status === "paid" && t.customer_email);
      setOrders(paid);
      if (paid.length && !orderId) setOrderId(paid[0].session_id);
    }).catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await crmApi(`/reviews/${id}`, { method: "PATCH", body: { status } }); load(); };
  const requestReview = async () => {
    setNote("");
    try {
      await crmApi("/reviews/request", { method: "POST", body: { session_id: orderId } });
      setNote("Review request email sent");
    } catch (e) { setNote(e.response?.data?.detail || "Failed"); }
  };
  const approved = reviews.filter((r) => r.status === "approved");
  const avg = approved.length ? (approved.reduce((s, r) => s + r.rating, 0) / approved.length).toFixed(1) : null;
  return (
    <div className="space-y-6" data-testid="crm-reviews">
      <Card>
        <SectionTitle>Request a review</SectionTitle>
        <div className="flex flex-wrap gap-2.5 items-center">
          <select value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="review-order-select"
            className="bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-sm outline-none min-w-64">
            {orders.length === 0 && <option value="">No paid orders with email</option>}
            {orders.map((o) => (
              <option key={o.session_id} value={o.session_id}>
                {o.customer_email} — CA${((o.amount || 0) / 100).toLocaleString()} {o.review_requested ? "(already sent)" : ""}
              </option>
            ))}
          </select>
          <Btn onClick={requestReview} disabled={!orderId} data-testid="review-request-send">Send review request</Btn>
        </div>
        {note && <p className="text-sm text-[#f97316] mt-3" data-testid="review-note">{note}</p>}
        <p className="text-xs text-slate-600 mt-3">The customer gets an email with a one-minute review link. Approved reviews power the storefront section and Google star ratings.</p>
      </Card>
      <Card>
        <SectionTitle>Reviews {avg && <span className="text-[#f97316] text-base ml-2">★ {avg} avg · {approved.length} approved</span>}</SectionTitle>
        {reviews.length === 0 ? <Empty>No reviews yet — send your first request above</Empty> : (
          <ul className="space-y-3">
            {reviews.map((r) => (
              <li key={r.id} className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 flex flex-wrap items-center gap-3">
                <span className="text-[#f97316] text-sm">{"★".repeat(r.rating)}<span className="text-slate-700">{"★".repeat(5 - r.rating)}</span></span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-slate-200">{r.text}</span>
                  <div className="text-xs text-slate-500 mt-1">{r.name || "Verified owner"} · order {r.order_ref}</div>
                </div>
                <Pill tone={r.status === "approved" ? "green" : r.status === "rejected" ? "red" : "amber"}>{r.status}</Pill>
                {r.status === "pending" && (
                  <span className="space-x-2">
                    <Btn onClick={() => setStatus(r.id, "approved")} data-testid="review-approve">Approve</Btn>
                    <Btn onClick={() => setStatus(r.id, "rejected")} data-testid="review-reject">Reject</Btn>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function AutomationsTab() {
  const [rules, setRules] = useState(null);
  const [result, setResult] = useState(null);
  const load = useCallback(() => crmApi("/automations").then(setRules).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (k) => { const next = { ...rules, [k]: !rules[k] }; setRules(next); await crmApi("/automations", { method: "PUT", body: next }); };
  const run = async () => setResult(await crmApi("/automations/run", { method: "POST" }));
  if (!rules) return <Empty>Loading…</Empty>;
  return (
    <Card data-testid="crm-automations">
      <SectionTitle>Marketing automations</SectionTitle>
      <div className="space-y-3 mb-8">
        {Object.entries(RULE_LABELS).map(([k, label]) => (
          <div key={k} className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5">
            <span className="text-sm text-slate-200">{label}</span>
            <button onClick={() => toggle(k)} data-testid={`rule-${k}`}
              className={`w-12 h-6 rounded-full transition-colors relative ${rules[k] ? "bg-[#f97316]" : "bg-white/10"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${rules[k] ? "left-6" : "left-0.5"}`} />
            </button>
          </div>
        ))}
      </div>
      <Btn onClick={run} data-testid="automations-run">Run automations now</Btn>
      <span className="text-xs text-slate-500 ml-4">Also runs automatically every 15 minutes on the server.</span>
      {result && (
        <p className="text-sm text-[#f97316] mt-4" data-testid="automations-result">
          Sent {result.recovery_sent} recovery · {result.thankyou_sent} thank-you · {result.vip_tagged} VIP tagged
        </p>
      )}
    </Card>
  );
}

export function CopilotTab() {
  const [cards, setCards] = useState(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => { crmApi("/copilot").then(setCards).catch(() => setCards([])); }, []);
  const makeDraft = async (card) => {
    const d = await crmApi("/copilot/draft", { method: "POST", body: { type: card.type, customer_email: card.detail } });
    setDraft(d);
  };
  if (!cards) return <Empty>Loading copilot…</Empty>;
  return (
    <div className="space-y-6" data-testid="crm-copilot">
      <Card>
        <SectionTitle>AI Sales Copilot</SectionTitle>
        {cards.length === 0 ? <Empty>No recommendations right now — the pipeline is healthy</Empty> : (
          <div className="space-y-3">
            {cards.map((c) => (
              <div key={c.id} className="flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5">
                <Pill tone={c.priority === "high" ? "red" : c.priority === "medium" ? "amber" : "slate"}>{c.priority}</Pill>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{c.title}</div>
                  <div className="text-xs text-slate-500">{c.detail}</div>
                </div>
                <Btn onClick={() => makeDraft(c)} data-testid="copilot-draft">Draft</Btn>
              </div>
            ))}
          </div>
        )}
      </Card>
      {draft && (
        <Card data-testid="copilot-draft-result">
          <SectionTitle>Draft: {draft.subject}</SectionTitle>
          <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{draft.body}</p>
          {draft.to && <p className="text-xs text-slate-500 mt-3">To: {draft.to}</p>}
        </Card>
      )}
    </div>
  );
}

export function InventoryTab() {
  const [data, setData] = useState(null);
  useEffect(() => { crmApi("/inventory").then(setData).catch(() => {}); }, []);
  if (!data) return <Empty>Loading…</Empty>;
  return (
    <Card data-testid="crm-inventory">
      <SectionTitle>Inventory</SectionTitle>
      <Pill tone="amber">placeholder — stock modeling not connected</Pill>
      <div className="mt-5 space-y-3">
        {data.items.map((i) => (
          <div key={i.sku} className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5">
            <div>
              <div className="text-sm font-semibold">{i.name}</div>
              <div className="font-tech text-[10px] text-slate-500 tracking-wider">{i.sku}</div>
            </div>
            <span className="text-xs text-slate-500">{i.note}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}
